/**
 * ProjectList Component - List of projects in sidebar
 */
import React, { useEffect, useState } from 'react';
import { useProjectStore } from '../../stores';
import DuplicateProjectDialog, { DuplicateOptions } from '../Dialogs/DuplicateProjectDialog';
import TrashDialog from '../Dialogs/TrashDialog';
import './ProjectList.css';

export const ProjectList: React.FC = () => {
  const {
    projects,
    currentProject,
    loading,
    error,
    fetchProjects,
    setCurrentProject,
    createProject,
    updateProject,
    deleteProject,
    duplicateProject,
    clearError,
  } = useProjectStore();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [duplicatingProject, setDuplicatingProject] = useState<{ id: number; name: string } | null>(null);
  const [showTrash, setShowTrash] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      await createProject({
        name: newProjectName,
        description: newProjectDescription || undefined,
      });
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateForm(false);
    } catch (error) {
      // Error is handled by store
    }
  };

  const handleDeleteProject = async (id: number, name: string) => {
    if (window.confirm(`Naozaj chcete vymazať projekt "${name}"?`)) {
      try {
        await deleteProject(id);
      } catch (error) {
        // Error is handled by store
      }
    }
  };

  const handleStartEdit = (project: any) => {
    setEditingProjectId(project.id);
    setEditProjectName(project.name);
    setEditProjectDescription(project.description || '');
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditProjectName('');
    setEditProjectDescription('');
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProjectName.trim() || editingProjectId === null) return;

    try {
      await updateProject(editingProjectId, {
        name: editProjectName,
        // Prazdny retazec, nie undefined: undefined sa pri odoslani vynecha,
        // takze zmazany popis sa nikdy neulozil a projekt si drzal stary.
        description: editProjectDescription.trim(),
      });
      setEditingProjectId(null);
      setEditProjectName('');
      setEditProjectDescription('');
    } catch (error) {
      // Error is handled by store
    }
  };

  const handleDuplicateProject = (id: number, name: string) => {
    setDuplicatingProject({ id, name });
  };

  const handleConfirmDuplicate = async (options: DuplicateOptions) => {
    if (!duplicatingProject) return;

    try {
      const duplicated = await duplicateProject(duplicatingProject.id, options);
      alert(`Projekt bol úspešne duplikovaný ako "${duplicated.name}"`);
      setDuplicatingProject(null);
    } catch (error) {
      // Error is handled by store
      setDuplicatingProject(null);
    }
  };

  return (
    <div className="project-list">
      <div className="project-list-header">
        <h3>Projekty</h3>
        <div className="header-buttons">
          <button
            className="btn-trash"
            onClick={() => setShowTrash(true)}
            title="Kôš"
          >
            🗑️
          </button>
          <button
            className="btn-create"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕' : '+'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={clearError}>✕</button>
        </div>
      )}

      {showCreateForm && (
        <form className="create-form" onSubmit={handleCreateProject}>
          <input
            type="text"
            placeholder="Názov projektu"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            autoFocus
          />
          <textarea
            placeholder="Popis (voliteľný)"
            value={newProjectDescription}
            onChange={(e) => setNewProjectDescription(e.target.value)}
            rows={3}
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              Vytvoriť
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowCreateForm(false)}
            >
              Zrušiť
            </button>
          </div>
        </form>
      )}

      {loading && projects.length === 0 ? (
        <div className="loading">Načítavajú sa projekty...</div>
      ) : (
        <div className="project-items">
          {projects.length === 0 ? (
            <div className="empty-state">
              <p>Zatiaľ žiadne projekty</p>
              <p className="empty-state-hint">Kliknite na + pre vytvorenie prvého projektu</p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                className={`project-item ${
                  currentProject?.id === project.id ? 'active' : ''
                }`}
              >
                {editingProjectId === project.id ? (
                  <form className="edit-form" onSubmit={handleSaveEdit} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder="Názov projektu"
                      value={editProjectName}
                      onChange={(e) => setEditProjectName(e.target.value)}
                      autoFocus
                    />
                    <textarea
                      placeholder="Popis (voliteľný)"
                      value={editProjectDescription}
                      onChange={(e) => setEditProjectDescription(e.target.value)}
                      rows={2}
                    />
                    <div className="form-actions">
                      <button type="submit" className="btn-primary" disabled={loading}>
                        Uložiť
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleCancelEdit}
                      >
                        Zrušiť
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="project-item-content" onClick={() => setCurrentProject(project)}>
                      <div className="project-item-name">{project.name}</div>
                      {project.description && (
                        <div className="project-item-description">
                          {project.description}
                        </div>
                      )}
                      <div className="project-item-date">
                        Vytvorené: {new Date(project.created_at).toLocaleDateString('sk-SK')}
                      </div>
                    </div>
                    <div className="project-item-actions">
                      <button
                        className="btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEdit(project);
                        }}
                        title="Upraviť projekt"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateProject(project.id, project.name);
                        }}
                        title="Duplikovať projekt"
                      >
                        📋
                      </button>
                      <button
                        className="btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id, project.name);
                        }}
                        title="Vymazať projekt"
                      >
                        🗑️
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {duplicatingProject && (
        <DuplicateProjectDialog
          projectName={duplicatingProject.name}
          onConfirm={handleConfirmDuplicate}
          onClose={() => setDuplicatingProject(null)}
        />
      )}

      <TrashDialog
        isOpen={showTrash}
        onClose={() => setShowTrash(false)}
        onProjectRestored={() => {
          fetchProjects();
          setShowTrash(false);
        }}
      />
    </div>
  );
};

